/**
 * TipTap Signature Block (#970).
 *
 * The signature lives in the composer as ONE locked block that holds the
 * builder's sanitised HTML and renders it as-is, so the layout, colours,
 * fonts, sizes and photo place the preview shows are what the message holds
 * and what is sent. It is not edited inline: the user switches it, picks None,
 * or selects the block and deletes it.
 *
 * Parsed only from `<div data-ncrm-signature="<signature id>">`, so no other
 * editor content (templates, deal rooms, products) can turn into a block.
 * Serialised as that same wrapper around the stored HTML, which is DOMPurify's
 * own output, so `getHTML(setContent(x)) === x` holds after the first pass.
 * Replace-not-stack finds the block by its wrapper, never by string match.
 */
import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { SIGNATURE_BLOCK_ATTR, sanitizeSignatureHtml } from '../lib/signatureSanitize';

const SignatureBlock = Node.create({
    name: 'signatureBlock',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: false,

    addAttributes() {
        return {
            signatureId: {
                default: '',
                parseHTML: (el) => el.getAttribute(SIGNATURE_BLOCK_ATTR) || '',
                rendered: false,
            },
            html: {
                default: '',
                parseHTML: (el) => sanitizeSignatureHtml(el.innerHTML),
                rendered: false,
            },
        };
    },

    parseHTML() {
        return [{ tag: `div[${SIGNATURE_BLOCK_ATTR}]` }];
    },

    // The block's links are the signature's, for the recipient. In the
    // composer a click must not navigate away from the draft.
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('signatureBlockLinks'),
                props: {
                    handleDOMEvents: {
                        click: (_view, event) => {
                            const link = event.target?.closest?.(`[${SIGNATURE_BLOCK_ATTR}] a`);
                            if (!link) return false;
                            event.preventDefault();
                            return true;
                        },
                    },
                },
            }),
        ];
    },

    // A ProseMirror DOMOutputSpec may be a DOM node, "interpreted as itself".
    // That is how the stored HTML is emitted verbatim instead of as editor nodes.
    renderHTML({ node }) {
        const dom = document.createElement('div');
        dom.setAttribute(SIGNATURE_BLOCK_ATTR, node.attrs.signatureId || '');
        dom.innerHTML = node.attrs.html || '';
        return dom;
    },
});

export default SignatureBlock;
